import React, { useEffect, useState } from "react";
import { Calendar, MapPin, ExternalLink, Users, X, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const RsvpModal = ({ event, onClose, onSuccess }) => {
    const [form, setForm] = useState({ name: "", email: "", party_size: 1, notes: "" });
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post(`/events/${event.id}/rsvp`, {
                ...form,
                party_size: parseInt(form.party_size) || 1,
            });
            toast.success("RSVP confirmed!");
            onSuccess();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "RSVP failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="rsvp-modal">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <p className="text-xs uppercase tracking-wider text-[#E53935] font-bold mb-1">RSVP</p>
                        <h3 className="font-heading text-2xl text-[#1C252E] font-medium">{event.title}</h3>
                        <p className="text-sm text-[#4A5568] mt-1">
                            {new Date(event.starts_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="space-y-3">
                    <input required placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rsvp-name" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                    <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rsvp-email" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-1">Party size</label>
                        <input required type="number" min="1" max="20" value={form.party_size} onChange={(e) => setForm({ ...form, party_size: e.target.value })} data-testid="rsvp-party" className="w-full rounded-xl border border-black/10 px-4 py-3" />
                    </div>
                    <textarea placeholder="Notes (allergies, accessibility, etc.)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rsvp-notes" className="w-full rounded-xl border border-black/10 px-4 py-3 resize-none" />
                    <button type="submit" disabled={submitting} data-testid="rsvp-submit" className="w-full bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full py-3 font-medium disabled:opacity-60">
                        {submitting ? "Confirming…" : "Confirm RSVP"}
                    </button>
                </form>
            </div>
        </div>
    );
};

const EventCalendar = () => {
    const [events, setEvents] = useState([]);
    const [counts, setCounts] = useState({});
    const [activeEvent, setActiveEvent] = useState(null);

    const loadCounts = async (evs) => {
        const map = {};
        await Promise.all(
            evs.map(async (e) => {
                try {
                    const { data } = await api.get(`/rsvps/count/${e.id}`);
                    map[e.id] = data;
                } catch {}
            })
        );
        setCounts(map);
    };

    useEffect(() => {
        api.get("/events/public").then(async (r) => {
            setEvents(r.data);
            await loadCounts(r.data);
        }).catch(() => {});
    }, []);

    // Show a small friendly empty state instead of silently hiding the section.
    if (events.length === 0) {
        return (
            <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="events-section-empty">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" strokeWidth={2} />
                    School calendar
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight mb-2">
                    Upcoming events
                </h2>
                <p className="text-sm text-[#4A5568]">No upcoming public events yet.</p>
            </section>
        );
    }

    return (
        <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="events-section">
            <div className="mb-8">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" strokeWidth={2} />
                    School calendar
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight">
                    Upcoming events
                </h2>
            </div>
            <div className="space-y-3" data-testid="events-list">
                {events.map((e) => {
                    const d = new Date(e.starts_at);
                    const c = counts[e.id] || { party_total: 0, rsvp_count: 0 };
                    return (
                        <div
                            key={e.id}
                            data-testid={`event-${e.id}`}
                            className="bg-white border border-black/5 rounded-2xl p-5 flex items-start gap-5 hover:border-[#E53935]/40 transition-all"
                        >
                            <div className="flex-shrink-0 w-16 text-center bg-[#F4F1EA] rounded-xl py-3">
                                <p className="text-xs uppercase tracking-wider text-[#E53935] font-bold">
                                    {d.toLocaleDateString(undefined, { month: "short" })}
                                </p>
                                <p className="font-heading text-2xl text-[#1C252E] font-medium leading-none mt-1">
                                    {d.getDate()}
                                </p>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-heading text-lg text-[#1C252E] font-medium leading-tight">{e.title}</p>
                                {e.description && (
                                    <p className="text-sm text-[#4A5568] mt-1 line-clamp-2">{e.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-[#4A5568] flex-wrap">
                                    <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" strokeWidth={1.5} />
                                        {d.toLocaleString(undefined, { weekday: "long", hour: "numeric", minute: "2-digit" })}
                                    </span>
                                    {e.location && (
                                        <span className="flex items-center gap-1">
                                            <MapPin className="w-3 h-3" strokeWidth={1.5} />
                                            {e.location}
                                        </span>
                                    )}
                                    {c.party_total > 0 && (
                                        <span className="flex items-center gap-1" data-testid={`event-rsvp-count-${e.id}`}>
                                            <Users className="w-3 h-3" strokeWidth={1.5} />
                                            {c.party_total} attending
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                onClick={() => setActiveEvent(e)}
                                data-testid={`event-rsvp-btn-${e.id}`}
                                className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-5 py-2.5 text-sm font-medium transition-all whitespace-nowrap"
                            >
                                <Check className="w-4 h-4" strokeWidth={2} />
                                RSVP
                            </button>
                        </div>
                    );
                })}
            </div>

            {activeEvent && (
                <RsvpModal
                    event={activeEvent}
                    onClose={() => setActiveEvent(null)}
                    onSuccess={() => loadCounts(events)}
                />
            )}
        </section>
    );
};

export default EventCalendar;
